import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import { Flex } from "components/Flex"
import NoValue from "components/NoValue/NoValue"
import { ArticleType } from "config/constants"
import useArticleLevels from "pages/ArticlePage/ArticleLevels/useArticleLevels"

import { ArticleAny, ISummarySection } from "@/types"
import Button from "components/Button/Button"

interface IUseArticleDetailsBaseProps {
    article: ArticleAny
    onEditInspectionSetting?: () => void
}

export default function useArticleDetailsBase({
    article,
    onEditInspectionSetting
}: IUseArticleDetailsBaseProps) {
    const { t } = useTranslation()

    const {
        initArticleLevels,
        highLevelArticle,
        lowLevelArticles,
    } = useArticleLevels()

    useEffect(() => {
        initArticleLevels({
            highLevelArticleNumber: article.highLevelArticleNumber || null,
            lowLevelArticleNumbers: article.lowLevelArticleNumbers,
        })
    }, [article, initArticleLevels])

    const details: ISummarySection[] = useMemo(() => {

        return [
            {
                title: t('article.basicData'),
                items: [
                    {
                        label: t('article.brandDescription'),
                        value: article.manufacturerDescription,
                        labelTestId: "article-manufacturer-label",
                        valueTestId: "article-manufacturer"
                    },
                    {
                        label: t('article.brandArticleNo'),
                        value: article.manufacturerArticleNo,
                        labelTestId: "article-manufacturer-article-number-label",
                        valueTestId: "article-manufacturer-article-number"
                    },
                    ...((article.articleType === ArticleType.machine && !article.isExtManufacturer)
                        ? []
                        : [
                            {
                                label: t('article.higherLevelArticle'),
                                value: article.highLevelArticleNumber
                                    ? (highLevelArticle
                                        ? <div className="font-regular">
                                            <ArticleDescription
                                                articleDescription={highLevelArticle.description}
                                                articleId={highLevelArticle.id}
                                            />
                                        </div>
                                        : <NoValue>{t('common.loading')}</NoValue>
                                    )
                                    : <NoValue />
                            },
                            {
                                label: t('article.lowerLevelArticle'), value: article.lowLevelArticleNumbers.length
                                    ? (lowLevelArticles.length
                                        ? <div className="font-regular">
                                            {lowLevelArticles.map((lowLevelArticle, lowLevelArticleIndex) => (
                                                <Flex inline key={lowLevelArticle.id}>
                                                    {lowLevelArticleIndex !== 0 && <span>&nbsp;</span>}
                                                    <ArticleDescription
                                                        articleDescription={lowLevelArticle.description}
                                                        articleId={lowLevelArticle.id}
                                                    />
                                                    {lowLevelArticleIndex < lowLevelArticles.length - 1 && ','}
                                                </Flex>
                                            ))}
                                        </div>
                                        : <NoValue>{t('common.loading')}</NoValue>
                                    )
                                    : <NoValue />
                            },
                        ]
                    ),
                    {
                        label: t('article.articleSequence'),
                        value: !!article.sequence
                                ? <div>{article.sequence}</div>
                                : <NoValue />
                    },
                    {
                        label: t('article.inspectionRequired'),
                        value: <>{article.inspectionRequired ? t('common.yes') : t('common.no')}
                            {onEditInspectionSetting && <Button theme='link' className="ml-1 font-regular" label={t('common.edit')} onClick={onEditInspectionSetting} />}
                        </>,
                    }
                ]
            }
        ]
    }, [article.articleType, article.highLevelArticleNumber, article.inspectionRequired, article.isExtManufacturer, article.lowLevelArticleNumbers.length, article.manufacturerArticleNo, article.manufacturerDescription, highLevelArticle, lowLevelArticles, t, onEditInspectionSetting])

    return details
}
